import { IconPencil, IconShare, IconTrash } from '@tabler/icons-react';
import dayjs, { Dayjs } from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { QRCodeModal } from 'src/Components/Modals/QRCodeModal';
import { getCategoryColor } from 'src/Components/PracticalInformation/practicalInformationTypeColor.helper';
import { AttachmentsViewer } from 'src/Components/Shared/AttachmentsViewer';
import { KeyValueLine } from 'src/Components/Shared/KeyValueLine';
import { RowActions } from 'src/Components/Shared/RowActions';
import { useCreatePracticalInformationExport } from 'src/Hooks/Mutations/useCreatePracticalInformationExport';
import { useDeletePracticalInformation } from 'src/Hooks/Mutations/useDeletePracticalInformation';
import useFetchPracticalInformation from 'src/Hooks/Queries/useFetchPracticalInformation';
import { useCanCreate } from 'src/Hooks/Shared/useHasPermission';
import { useIsAssetAboveQuotas } from 'src/Hooks/Shared/useIsAssetAboveQuotas';
import { CreatePracticalInformationFieldDtoFieldTypeEnum } from 'src/Services/API/generated';
import { humanizeRRule, isRecurrenceDto } from 'src/Services/Shared/humanizeRRule';

import styles from './PracticalInformationDetails.module.scss';
import { Props } from './PracticalInformationDetails.types';

const EMPTY_VALUE = '—';

// Format an { hour, minute } object as "8h30" (fr) or "08:30" (en).
const formatClockObject = (value: Record<string, unknown>, language: string): string | null => {
  const hour = value.hour;
  const minute = value.minute;
  if (typeof hour !== 'number') return null;
  const min = typeof minute === 'number' ? minute : 0;
  if (language.startsWith('fr')) {
    return min === 0 ? `${hour}h` : `${hour}h${String(min).padStart(2, '0')}`;
  }
  return `${String(hour).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
};

// Turn any field value into a printable string. Never lets an object reach React as a child.
const renderFieldValue = (value: unknown, language: string): string => {
  if (value === null || value === undefined) return EMPTY_VALUE;
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  if (value instanceof Date) return dayjs(value).locale(language).format('LL');
  if (dayjs.isDayjs(value)) return (value as Dayjs).locale(language).format('LL');
  if (Array.isArray(value)) {
    const parts = value.map(v => renderFieldValue(v, language)).filter(v => v !== EMPTY_VALUE);
    return parts.length > 0 ? parts.join(', ') : EMPTY_VALUE;
  }
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const clock = formatClockObject(record, language);
    if (clock !== null) return clock;
    for (const key of ['label', 'name', 'value', 'time'] as const) {
      const candidate = record[key];
      if (typeof candidate === 'string' && candidate.length > 0) return candidate;
      if (typeof candidate === 'number') return String(candidate);
    }
    return EMPTY_VALUE;
  }
  return EMPTY_VALUE;
};

const PracticalInformationDetails = ({ practicalInformationId, onClose }: Props) => {
  const { data: information } = useFetchPracticalInformation(practicalInformationId);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { mutate: deleteInformation } = useDeletePracticalInformation({
    onMutate: () => {
      onClose?.();
      return undefined;
    },
  });
  const { mutate: createExport, isLoading: isCreatingExport } = useCreatePracticalInformationExport(
    {
      onSuccess: data => {
        setExportUrl(data.publicUrl);
        setShowQRCodeModal(true);
      },
    }
  );

  const [showQRCodeModal, setShowQRCodeModal] = useState(false);
  const [exportUrl, setExportUrl] = useState<string | undefined>(undefined);

  const { hasPermission: canExport } = useCanCreate('entityExport');
  const assetAboveQuotas = useIsAssetAboveQuotas();

  const categoryColor = information ? getCategoryColor(information.type) : undefined;

  return (
    information && (
      <div className={styles.container}>
        <QRCodeModal
          open={showQRCodeModal}
          url={exportUrl ?? ''}
          onClose={() => setShowQRCodeModal(false)}
          exportName={
            information.subtype
              ? t(`practicalInformation.subtypes.${information.subtype}`)
              : (information.title ?? '')
          }
        />
        {categoryColor && (
          <div className={styles.categoryBar} style={{ backgroundColor: categoryColor.hex }} />
        )}
        <div className={styles.topLine}>
          <div className={styles.title}>
            {information?.subtype
              ? t(`practicalInformation.subtypes.${information.subtype}`)
              : information?.title}
          </div>
          <RowActions
            actions={[
              {
                key: 'share',
                label: t('practicalInformation.menu.share', "Partager l'info pratique"),
                icon: <IconShare size={18} />,
                hidden: !canExport,
                disabled: isCreatingExport,
                onClick: () => createExport({ entityIds: [practicalInformationId] }),
              },
              {
                key: 'edit',
                label: t('practicalInformation.menu.edit', 'Modifier'),
                icon: <IconPencil size={18} />,
                hidden: information?.isSystem,
                disabled: assetAboveQuotas,
                onClick: () =>
                  navigate('edit', { state: { disableAnimation: true }, replace: true }),
              },
              {
                key: 'delete',
                label: t('practicalInformation.menu.delete', 'Supprimer'),
                icon: <IconTrash size={18} />,
                variant: 'danger',
                hidden: information?.isSystem,
                onClick: () => deleteInformation(practicalInformationId),
              },
            ]}
          />
        </div>
        {information?.fields
          .filter(field => Boolean(field.value))
          .map(field => (
            <KeyValueLine
              propertyKey={t(`practicalInformation.placeholders.${field.key}`)}
              value={
                field.fieldType === CreatePracticalInformationFieldDtoFieldTypeEnum.Date
                  ? typeof field.value === 'string' || typeof field.value === 'number'
                    ? dayjs(field.value).locale(i18n.language).format('LL')
                    : renderFieldValue(field.value, i18n.language)
                  : field.fieldType === CreatePracticalInformationFieldDtoFieldTypeEnum.Schedule &&
                      isRecurrenceDto(field.value)
                    ? humanizeRRule(field.value)
                    : renderFieldValue(field.value, i18n.language)
              }
            />
          ))}
        {information?.reminderFieldKey && (
          <KeyValueLine
            propertyKey={t('practicalInformation.reminder.detailLabel', 'Rappel')}
            value={
              information.reminderTimeOfDay
                ? renderFieldValue(information.reminderTimeOfDay, i18n.language)
                : t('practicalInformation.reminder.detailValueNoTime', 'Actif')
            }
          />
        )}
        {information?.attachments && information.attachments.length > 0 && (
          <AttachmentsViewer attachments={information.attachments} />
        )}
      </div>
    )
  );
};

export default PracticalInformationDetails;
